import React, { createContext, useContext, useState, useEffect } from 'react';
import { trackDarkModeToggle } from '../utils/analyticsUtils';

const DarkModeContext = createContext();

export const useDarkMode = () => {
  const context = useContext(DarkModeContext);
  if (!context) {
    throw new Error('useDarkMode must be used within a DarkModeProvider');
  }
  return context;
};

export const DarkModeProvider = ({ children }) => {
  // Three states: 'light', 'dark', 'system'
  const [mode, setMode] = useState(() => {
    const savedMode = localStorage.getItem('darkMode');
    return savedMode || 'system';
  });

  const [isDark, setIsDark] = useState(false);

  // Listen to system preference changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e) => {
      if (mode === 'system') {
        setIsDark(e.matches);
      }
    };

    // Set initial value
    if (mode === 'system') {
      setIsDark(mediaQuery.matches);
    } else {
      setIsDark(mode === 'dark');
    }

    // Listen for changes
    mediaQuery.addEventListener('change', handleChange);

    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [mode]);

  // Apply dark class to HTML element
  useEffect(() => {
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDark]);

  const cycleMode = () => {
    const modes = ['light', 'dark', 'system'];
    const currentIndex = modes.indexOf(mode);
    const nextMode = modes[(currentIndex + 1) % modes.length];
    setMode(nextMode);
    localStorage.setItem('darkMode', nextMode);

    // Track dark mode toggle
    trackDarkModeToggle(nextMode === 'dark' || (nextMode === 'system' && isDark));
  };

  const setModeTo = (next) => {
    if (!['light', 'dark', 'system'].includes(next) || next === mode) return;
    setMode(next);
    localStorage.setItem('darkMode', next);
    trackDarkModeToggle(next === 'dark' || (next === 'system' && isDark));
  };

  const value = {
    mode,
    isDark,
    cycleMode,
    setModeTo
  };

  return (
    <DarkModeContext.Provider value={value}>
      {children}
    </DarkModeContext.Provider>
  );
};
